import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NetstaffLogo } from "@/components/NetstaffLogo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { POSITION_NAMES, SHIFT_TYPES } from "@/lib/constants";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function RequestStaffPublic() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    facility_name: "", facility_type: "Hospital", contact_person: "", contact_number: "", contact_email: "",
    city: "Toronto", address: "", postal_code: "",
    position: "Personal Support Worker", count: 1, shift_date: "", start_time: "07:00", end_time: "15:00",
    shift_type: "Day", urgency: "standard", notes: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: f } = await api.post("/facilities", {
        name: form.facility_name, facility_type: form.facility_type,
        address: form.address, city: form.city, province: "Ontario",
        postal_code: form.postal_code, contact_person: form.contact_person,
        contact_number: form.contact_number, billing_email: form.contact_email,
      });
      const [sh, sm] = form.start_time.split(":").map(Number);
      const [eh, em] = form.end_time.split(":").map(Number);
      const totalHours = Math.max(1, (eh + em / 60) - (sh + sm / 60));
      await api.post("/requests", {
        facility_id: f.id,
        position: form.position, count: Number(form.count),
        shift_date: form.shift_date, start_time: form.start_time, end_time: form.end_time,
        total_hours: totalHours, shift_type: form.shift_type, urgency: form.urgency,
        contact_person: form.contact_person, contact_number: form.contact_number,
        notes: form.notes, status: "submitted",
      });
      setSubmitted(true);
      toast.success("Request submitted. Our dispatch team will respond shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center p-6">
        <Card className="max-w-lg w-full text-center p-8">
          <CheckCircle2 className="w-16 h-16 text-brand-teal mx-auto" />
          <h2 className="mt-4 text-2xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Request received</h2>
          <p className="mt-3 text-slate-600">Our dispatch team will review and reach out with a quote within hours.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/"><Button variant="outline" data-testid="thank-back-home">Back to home</Button></Link>
            <Link to="/register"><Button className="bg-brand-navy hover:bg-brand-blue" data-testid="thank-register">Create facility account</Button></Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient py-10">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/" className="block mb-6"><NetstaffLogo /></Link>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Request Healthcare Staff</CardTitle>
            <p className="text-sm text-slate-500">Submit your staffing need — our dispatchers respond with a quote.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="text-xs uppercase tracking-wider text-brand-teal font-semibold">Facility</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Facility name</Label><Input value={form.facility_name} onChange={(e) => upd("facility_name", e.target.value)} required data-testid="req-facility-name" /></div>
                <div>
                  <Label>Facility type</Label>
                  <Select value={form.facility_type} onValueChange={(v) => upd("facility_type", v)}>
                    <SelectTrigger data-testid="req-facility-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Hospital", "Long-Term Care Home", "Retirement Home", "Clinic", "Community Health Centre", "Rehabilitation Facility", "Home Care Agency", "Dental Clinic"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Contact name</Label><Input value={form.contact_person} onChange={(e) => upd("contact_person", e.target.value)} required data-testid="req-contact-name" /></div>
                <div><Label>Contact phone</Label><Input value={form.contact_number} onChange={(e) => upd("contact_number", e.target.value)} required data-testid="req-contact-phone" /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Address</Label><Input value={form.address} onChange={(e) => upd("address", e.target.value)} required /></div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => upd("city", e.target.value)} required /></div>
                <div><Label>Postal code</Label><Input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} required /></div>
              </div>

              <div className="text-xs uppercase tracking-wider text-brand-teal font-semibold pt-4">Shift</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label>Position</Label>
                  <Select value={form.position} onValueChange={(v) => upd("position", v)}>
                    <SelectTrigger data-testid="req-position"><SelectValue /></SelectTrigger>
                    <SelectContent>{POSITION_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Headcount</Label><Input type="number" min={1} value={form.count} onChange={(e) => upd("count", e.target.value)} data-testid="req-count" /></div>
                <div>
                  <Label>Urgency</Label>
                  <Select value={form.urgency} onValueChange={(v) => upd("urgency", v)}>
                    <SelectTrigger data-testid="req-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="emergency">Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-4 gap-3">
                <div><Label>Shift date</Label><Input type="date" value={form.shift_date} onChange={(e) => upd("shift_date", e.target.value)} required data-testid="req-date" /></div>
                <div><Label>Start time</Label><Input type="time" value={form.start_time} onChange={(e) => upd("start_time", e.target.value)} required /></div>
                <div><Label>End time</Label><Input type="time" value={form.end_time} onChange={(e) => upd("end_time", e.target.value)} required /></div>
                <div>
                  <Label>Shift type</Label>
                  <Select value={form.shift_type} onValueChange={(v) => upd("shift_type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{SHIFT_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Special requirements, location notes, skills needed…" data-testid="req-notes" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-brand-navy hover:bg-brand-blue h-11" data-testid="req-submit-btn">
                {loading ? "Submitting…" : "Submit staffing request"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
